export default class RequestPin {
  constructor() {
    this.modal = new theme.Modals('RequestPinModal', 'request-pin-modal', {
      closeOffContentClick: false,
    });
    this.modalNode = this.modal.$modal[0];
    if (!this.modalNode) return;

    // Shared
    this.screens = this.modalNode.querySelectorAll(
      '.request-pin-modal__screen'
    );
    this.screen1 = this.modalNode.querySelector(
      '.request-pin-modal__screen--1'
    );
    this.screen2 = this.modalNode.querySelector(
      '.request-pin-modal__screen--2'
    );
    this.loader = this.modalNode.querySelector('.request-pin-modal__spinner');
    this.recaptchaContainer = this.modalNode.querySelector('#recaptcha-v2');
    this.disclaimer = this.modalNode.querySelector(
      '.request-pin-modal__disclaimer'
    );
    this.errorAreas = this.modalNode.querySelectorAll(
      '.request-pin-modal__error'
    );

    // Screen 1 Specific
    this.form = this.screen1.querySelector('form');

    // Screen 2 Specific
    this.responseMessage = this.screen2.querySelector(
      '.request-pin-modal__response-message'
    );
    this.resendButton = this.screen2.querySelector(
      '.request-pin-modal__response-button--resend'
    );
    this.backButton = this.screen2.querySelector(
      '.request-pin-modal__response-back'
    );

    // Properties
    this.activeScreen = 1;
    this.phone = null;
    this.recaptchaV2 = null;
    this.pinRequestId = null;

    /**
     * init intl-tel-input
     * @see https://www.twilio.com/blog/international-phone-number-input-html-javascript
     */
    this.phoneInput = window.intlTelInput(
      this.modalNode.querySelector('#phone-number'),
      {
        utilsScript:
          'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
        onlyCountries: ['ca'],
        allowDropdown: false,
      }
    );

    this.ably = new Ably.Realtime(theme.campaignData.keys.ably);

    this.init();
  }

  changeScreen(number) {
    // Hide both screens.
    this.screens.forEach((screen) => {
      screen.style.display = 'none';
    });

    this.recaptchaContainer.style.display = 'none';
    this.disclaimer.style.display = 'none';

    if (number === 1 || number === 2) {
      // Remove loader
      this.loader.style.display = 'none';

      // Show selected screen. Recaptcha should show/hide with either screen.
      this[`screen${number}`].style.display = 'block';
      this.recaptchaContainer.style.display = 'block';
      this.disclaimer.style.display = 'block';
      this.activeScreen = number;
    } else {
      // Show loader
      this.loader.style.display = 'block';
    }

    grecaptcha.enterprise.reset();
  }

  getRecaptchaToken() {
    return new Promise((resolve) => {
      grecaptcha.enterprise.ready(() => {
        const recaptcha_token = grecaptcha.enterprise.getResponse();
        // resolve the response from the recaptcha
        resolve({ recaptcha_token });
      });
    });
  }

  /**
   *
   * @param {string} message An HTML message. Any instances of "[phone]" will be replaced with the "phone" param
   * @param {string} phone the user's phone number
   */
  renderSuccessMessage(phone) {
    this.responseMessage.innerText = this.responseMessage.innerText.replace(
      '[phone]',
      phone
    );
    // grecaptcha.enterprise.reset();
    this.changeScreen(2);
  }

  /**
   *
   * @param {string} message An HTML message. Any instances of "[phone]" will be replaced with the "phone" param
   * @param {string} phone the user's phone number
   */
  renderError(message, phone = '') {
    this.errorAreas.forEach((errorArea) => {
      if (!!message) {
        errorArea.innerHTML = message.replace('[phone]', phone);

        // show message
        errorArea.style.display = 'block';
      } else {
        // hide message
        errorArea.style.display = 'none';
      }
    });
  }

  /**
   *
   * @param {string} phone the user's phone number
   * @todo consider a try catch block
   */
  async sendToApp(phone) {
    // clear any error messages
    this.renderError('');

    // get recaptcha tokens
    const recaptchaToken = await this.getRecaptchaToken();

    // hide screens
    this.changeScreen();

    try {
      // this needs to be async/await bc we need to use response and data in tandem
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: JSON.stringify({
          phone: phone,
          language: window.lang,
          referrer: document.referrer,
          ...recaptchaToken,
        }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        /**
         * A 422 means there are errors present (likely a blank recaptcha v2).
         * If the app returns a 422, bypass the generic error below, so we can continue through the flow to get the necessary data.
         * We need to get the specific error messages. This is handled further down the page.
         */
        if (response.status !== 422) {
          // generic error
          throw new Error(theme.campaignData.strings.genericError);
        }
      }

      const data = await response.json();

      /**
       * Jump in front of Ably if the app returns a 422
       */
      if (response.status === 422) {
        let errorString = '';

        for (const property in data.errors) {
          if (property.toLowerCase().includes('recaptcha')) {
            errorString = `${theme.campaignData.strings.recaptcha}<br>${errorString}`;
          } else {
            // dynamically generate any additional error messages
            errorString = `${property}: ${data.errors[property]}<br>${errorString}`;
          }
        }

        throw new Error(errorString);
      }

      // At this point, the data.status is "pending". Continue to show loading screen.

      let pinRequestIdString = data.id.toString();

      // Ably is expecting 'pin_request_X' formatting
      if (!pinRequestIdString.includes('pin_request_')) {
        pinRequestIdString = `pin_request_${data.id}`;
      }

      const channel = this.ably.channels.get(pinRequestIdString);
      this.pinRequestId = data.id;

      const ablyPromise = new Promise((resolve) => {
        channel.subscribe('push', (message) => {
          resolve(message.data);
        });
      });

      // wait for Ably's reponse before moving forward
      const messageData = await ablyPromise;

      if (messageData.status === 'sent') {
        // success messages
        this.renderSuccessMessage(phone);
      } else if (messageData.status === 'failed') {
        // specific errors
        if (messageData.error === 'no_pins_left') {
          throw new Error(theme.campaignData.strings.noPinsLeft);
        }

        if (messageData.error === 'recaptcha_failed') {
          throw new Error(theme.campaignData.strings.recaptcha);
        }

        if (messageData.error === '21211') {
          throw new Error(theme.campaignData.strings.invalidPhone);
        }

        // generic error
        throw new Error(theme.campaignData.strings.genericError);
      }
    } catch (error) {
      // failure
      this.renderError(error.message, phone);

      this.changeScreen(this.activeScreen);
    }
  }

  /**
   * do some frontend validation before sending the user's input off to the app
   */
  onFormSubmit() {
    try {
      // VALID
      if (this.phoneInput.isValidNumber()) {
        // cache phone number
        this.phone = this.phoneInput.getNumber();

        // send to app
        this.sendToApp(this.phone);
        return;
      }

      // INVALID
      const error = this.phoneInput.getValidationError();
      const validationErrorOptions = intlTelInputUtils.validationError;

      // throw errors before hitting the app, allowing users to fix their answers
      if (error === validationErrorOptions.IS_POSSIBLE) {
        // for testing, uncomment the below lines. this will allow you to test with phone numebrs like 1111111111 and 1212121212
        // TODO: don't forget to comment this back out

        // cache phone number
        this.phone = this.phoneInput.getNumber();

        // send to app
        this.sendToApp(this.phone);
        return;

        throw new Error('IS_POSSIBLE, BUT UNLIKELY');
      }

      if (error === validationErrorOptions.INVALID_COUNTRY_CODE) {
        throw new Error(theme.campaignData.strings.invalidCountryCode);
      }

      if (error === validationErrorOptions.NOT_A_NUMBER) {
        throw new Error(theme.campaignData.strings.notANumber);
      }

      if (error === validationErrorOptions.TOO_LONG) {
        throw new Error(theme.campaignData.strings.tooLong);
      }

      if (error === validationErrorOptions.TOO_SHORT) {
        throw new Error(theme.campaignData.strings.tooShort);
      }
    } catch (error) {
      this.renderError(error.message);
    }
  }

  initEventListeners() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();

      this.onFormSubmit();
    });

    this.resendButton.addEventListener('click', (e) => {
      // Send to app. Safe to bypass all validation (except recaptcha), because if we've made it this far, it's safe to assume the number has already been validated.
      this.sendToApp(this.phone);
    });

    this.backButton.addEventListener('click', (e) => {
      e.preventDefault();

      this.changeScreen(1);
    });
  }

  init() {
    grecaptcha.enterprise.ready(() => {
      this.recaptchaV2 = grecaptcha.enterprise.render('recaptcha-v2', {
        sitekey: theme.campaignData.keys.recaptcha,
      });

      this.initEventListeners();

      if (location.hash.includes('request-a-pin')) {
        this.modal.open();
      }
    });
  }
}
